import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  X, 
  Plus, 
  Building2, 
  ShoppingCart, 
  BookOpen, 
  Vote,
  Palette, 
  Phone, 
  Mail, 
  Globe,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

interface CreateCategoryTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTenantCreated: () => void;
}

interface CategoryTenantFormData {
  category: 'boutique' | 'presentation' | 'elearning' | 'vote';
  name: string;
  email: string;
  domain_name: string;
  logo_url: string;
  icon_url: string;
  bg_url: string;
}

const CATEGORY_OPTIONS = [
  {
    value: 'boutique',
    label: 'Boutique E-commerce',
    icon: ShoppingCart,
    description: 'Sites de vente en ligne avec panier et paiement',
    colors: { primary: '#fe8d17', secondary: '#008aef' },
    features: { shop: true, blog: false, events: false, contests: false }
  },
  {
    value: 'presentation',
    label: 'Site de Présentation',
    icon: Building2,
    description: 'Sites vitrine pour entreprises et professionnels',
    colors: { primary: '#1e40af', secondary: '#64748b' },
    features: { shop: false, blog: true, events: true, contests: false }
  },
  {
    value: 'elearning',
    label: 'Plateforme E-learning',
    icon: BookOpen,
    description: 'Plateformes de formation avec cours et quiz',
    colors: { primary: '#059669', secondary: '#0891b2' },
    features: { shop: false, blog: true, events: true, contests: false }
  },
  {
    value: 'vote',
    label: 'Système de Vote',
    icon: Vote,
    description: 'Plateformes de concours et votes en ligne',
    colors: { primary: '#dc2626', secondary: '#ea580c' },
    features: { shop: false, blog: false, events: true, contests: true }
  }
];

const initialFormData: CategoryTenantFormData = {
  category: 'boutique',
  name: '',
  email: '',
  domain_name: '',
  logo_url: '',
  icon_url: '',
  bg_url: ''
};

export const CreateCategoryTenantModal: React.FC<CreateCategoryTenantModalProps> = ({
  isOpen,
  onClose,
  onTenantCreated
}) => {
  const [formData, setFormData] = useState<CategoryTenantFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof CategoryTenantFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getSelectedCategoryInfo = () => {
    return CATEGORY_OPTIONS.find(cat => cat.value === formData.category) || CATEGORY_OPTIONS[0];
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Le nom du tenant est requis');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('L\'email est requis');
      return false;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Format d\'email invalide');
      return false;
    }

    // Validation domaine si fourni
    if (formData.domain_name && formData.domain_name.trim()) {
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(formData.domain_name)) {
        setError('Format de domaine invalide');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const categoryInfo = getSelectedCategoryInfo();

      // Construire la configuration du tenant
      const config = {
        category: formData.category,
        template: {
          header_variant: formData.category === 'boutique' ? 'modern' : 
                         formData.category === 'presentation' ? 'classic' :
                         formData.category === 'elearning' ? 'modern' : 'minimal',
          footer_variant: formData.category === 'boutique' ? 'full' :
                         formData.category === 'presentation' ? 'compact' :
                         formData.category === 'elearning' ? 'full' : 'minimal',
          pages: formData.category === 'boutique' ? ['home', 'catalog', 'product', 'cart', 'checkout', 'account'] :
                 formData.category === 'presentation' ? ['home', 'about', 'services', 'contact', 'portfolio'] :
                 formData.category === 'elearning' ? ['home', 'courses', 'quiz', 'progress', 'certificates', 'profile'] :
                 ['home', 'contests', 'vote', 'results', 'rules'],
          layout: formData.category === 'elearning' ? 'sidebar' : 'fullwidth'
        },
        features: categoryInfo.features,
        colors: {
          primary: categoryInfo.colors.primary,
          secondary: categoryInfo.colors.secondary,
          color1: categoryInfo.colors.primary,
          color2: categoryInfo.colors.secondary,
          color3: '#ffffff',
          color4: '#000000'
        },
        phone: '(+242) 06 000 0000',
        city: 'Pointe-Noire',
        country: 'République du Congo',
        country_code: '242',
        address: 'Adresse à définir, Pointe-Noire',
        domain_overrides: {}
      };

      // Ajouter les URLs d'images si fournies
      if (formData.logo_url) (config as any).logo = formData.logo_url;
      if (formData.icon_url) (config as any).icon = formData.icon_url;
      if (formData.bg_url) (config as any).bg = formData.bg_url;

      // Insérer le tenant
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .insert({
          name: formData.name.trim(),
          email: formData.email.trim(),
          logo: formData.logo_url || '',
          domain_name: formData.domain_name.toLowerCase().trim() || '',
          config: config,
          tenant_id: null
        })
        .select()
        .single();

      if (tenantError) throw tenantError;

      // Mettre à jour tenant_id pour qu'il soit égal à id
      const { error: updateError } = await supabase
        .from('tenants')
        .update({ tenant_id: tenantData.id })
        .eq('id', tenantData.id);

      if (updateError) throw updateError;

      setSuccess(`Tenant de catégorie "${categoryInfo.label}" créé avec succès ! ID: ${tenantData.id}`);
      
      // Notifier le parent
      onTenantCreated();
      
      // Fermer après un délai
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err: any) {
      setError(`Erreur lors de la création du tenant: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedCategory = getSelectedCategoryInfo();
  const SelectedIcon = selectedCategory.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Plus className="w-5 h-5 mr-2" />
              Créer un Tenant de Catégorie
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Créez un nouveau tenant avec template spécialisé
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <span className="text-green-700">{success}</span>
          </div>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Sélection de catégorie */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Catégorie de Tenant
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CATEGORY_OPTIONS.map((category) => {
                const Icon = category.icon;
                const isSelected = formData.category === category.value;
                
                return (
                  <div
                    key={category.value}
                    onClick={() => handleInputChange('category', category.value)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <Icon className={`w-5 h-5 mr-2 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                      <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {category.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{category.description}</p>
                    <div className="flex items-center mt-2 space-x-2">
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: category.colors.primary }}
                      />
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: category.colors.secondary }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Aperçu de la catégorie sélectionnée */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <SelectedIcon className="w-5 h-5 mr-2 text-gray-700" />
              <span className="font-medium">Catégorie sélectionnée: {selectedCategory.label}</span>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Features: {Object.entries(selectedCategory.features)
                .filter(([_, enabled]) => enabled)
                .map(([feature, _]) => feature)
                .join(', ')}</div>
              <div>Couleurs: {selectedCategory.colors.primary}, {selectedCategory.colors.secondary}</div>
            </div>
          </div>

          {/* Informations de base */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              Informations de Base
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du Tenant *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Boutiques E-commerce"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="boutiques@sis-cg.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Domaine Principal (optionnel)
              </label>
              <input
                type="text"
                value={formData.domain_name}
                onChange={(e) => handleInputChange('domain_name', e.target.value)}
                placeholder="boutiques.sis-cg.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Médias (optionnel) */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Palette className="w-5 h-5 mr-2" />
              Médias (Optionnel)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Logo
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Icône
                </label>
                <input
                  type="url"
                  value={formData.icon_url}
                  onChange={(e) => handleInputChange('icon_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Background
                </label>
                <input
                  type="url"
                  value={formData.bg_url}
                  onChange={(e) => handleInputChange('bg_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {loading ? 'Création...' : 'Créer le Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
